import PropTypes from 'prop-types';

import { AdditionalShippingOptionsPropType } from './additional-shipping-options';
import { BillToPartyPropType } from './bill-to-party';
import { CurrencyPropType } from './currency';
import { WeightPropType } from './weight';

export const ShippingOptionsPropType = PropTypes.shape({
  additionalOptions: PropTypes.arrayOf(AdditionalShippingOptionsPropType),
  dryIce: PropTypes.shape({
    weight: WeightPropType,
  }),
  includeReturnLabel: PropTypes.shape({
    labelProviderId: PropTypes.string,
    serviceId: PropTypes.string,
    packageTypeId: PropTypes.string,
  }),
  collectOnDelivery: PropTypes.shape({
    payment: CurrencyPropType,
    paymentType: PropTypes.string,
  }),
  dangerousGoods: PropTypes.shape({
    contactName: PropTypes.string,
    contactPhone: PropTypes.string,
  }),
  dangerousGoodsLegacy: PropTypes.shape({
    emergencyContact: PropTypes.string,
    goodsType: PropTypes.string,
  }),
  dangerousGoodsSimple: PropTypes.bool,
  dangerousGoodsPurolator: PropTypes.shape({
    class: PropTypes.string,
    mode: PropTypes.string,
  }),
  consequentialLoss: PropTypes.shape({
    value: PropTypes.number,
  }),
  emailNotification: PropTypes.shape({
    email: PropTypes.string,
  }),
  smsNotification: PropTypes.shape({
    phone: PropTypes.string,
  }),
  safeplace: PropTypes.shape({
    instructions: PropTypes.string,
  }),
  billToAccount: PropTypes.shape({
    billToParty: BillToPartyPropType,
    accountNumber: PropTypes.string,
    billingCountry: PropTypes.string,
    billingPostalCode: PropTypes.string,
  }),
  freightClassCode: PropTypes.string,
  shipperRelease: PropTypes.bool,
  saturdayDelivery: PropTypes.bool,
  containsAlcohol: PropTypes.bool,
  confirmation: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  conrimationEnumId: PropTypes.number,
  nonMachinable: PropTypes.bool,
  deliveredDutyPaid: PropTypes.bool,
  holdForPickup: PropTypes.bool,
  specialHandling: PropTypes.bool,
  dontNotifyMarketplace: PropTypes.bool,
  saturdayGuarantee: PropTypes.bool,
  localCollect: PropTypes.bool,
  goGreenPlus: PropTypes.bool,
  regulatedContentType: PropTypes.string,
  movementIndicator: PropTypes.string,
  serviceLevel: PropTypes.string,
  notAtRisk: PropTypes.bool,
  fedExShipperLoadAndCount: PropTypes.string,
  fedExBookingConfirmation: PropTypes.string,
  exportLicenceRequired: PropTypes.bool,
});

/**
 * @typedef {object} ShippingOptionsPropType
 * @property {object} dryIce
 * @property {WeightPropType} dryIce.weight
 * @property {object} includeReturnLabel
 * @property {string} includeReturnLabel.labelProviderId
 * @property {string} includeReturnLabel.serviceId
 * @property {string} includeReturnLabel.packageTypeId
 * @property {object} collectOnDelivery
 * @property {CurrencyPropType} collectOnDelivery.payment
 * @property {string} collectOnDelivery.paymentType
 * @property {object} dangerousGoods
 * @property {string} dangerousGoods.emergencyContact
 * @property {string} dangerousGoods.goodsType
 * @property {object} dangerousGoodsLegacy
 * @property {string} dangerousGoodsLegacy.emergencyContact
 * @property {string} dangerousGoodsLegacy.goodsType
 * @property {boolean} dangerousGoodsSimple
 * @property {object} dangerousGoodsPurolator
 * @property {string} dangerousGoodsPurolator.class
 * @property {string} dangerousGoodsPurolator.mode
 * @property {object} consequentialLoss
 * @property {number} consequentialLoss.value
 * @property {object} emailNotification
 * @property {string} emailNotification.email
 * @property {object} smsNotification
 * @property {string} smsNotification.phone
 * @property {object} safeplace
 * @property {string} safeplace.instructions
 * @property {object} billToAccount
 * @property {BillToPartyPropType} billToAccount.billToParty
 * @property {string} billToAccount.accountNumber
 * @property {string} billToAccount.billingCountry
 * @property {string} billToAccount.billingPostalCode
 * @property {boolean} freightClassCode
 * @property {boolean} shipperRelease
 * @property {boolean} saturdayDelivery
 * @property {boolean} containsAlcohol
 * @property {string|number} confirmation
 * @property {number} conrimationEnumId
 * @property {boolean} nonMachinable
 * @property {boolean} deliveredDutyPaid
 * @property {boolean} holdForPickup
 * @property {boolean} specialHandling
 * @property {boolean} dontNotifyMarketplace
 * @property {boolean} saturdayGuarantee
 * @property {boolean} localCollect
 * @property {boolean} goGreenPlus
 * @property {string} regulatedContentType
 * @property {string} movementIndicator
 * @property {boolean} notAtRisk
 * @property {string} fedExShipperLoadAndCount
 * @property {string} fedExBookingConfirmation
 * @property {boolean} exportLicenceRequired
 */
